/**
 * Product comparison page.
 *
 * Reads a pre-rendered JSON island (`x-ref="compareData"`) containing the
 * comparable products grouped by Product Series, then presents up to three
 * columns — two on mobile — each with a dropdown that swaps the product shown.
 * Columns start empty; picking a product loads its specifications.
 *
 * Specifications are not in the island (they are the bulk of the data); they
 * are fetched on demand from the REST endpoint named on the island element's
 * `data-specs-endpoint` attribute when a column's product changes, then cached
 * per product id. Each column renders its own product's specs as-is — there is
 * no alignment across columns.
 */

const MAX_COLUMNS_DEFAULT = 3;
const MOBILE_COLUMNS      = 2;

document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'comparePage', () => ({

		data         : { series: [], products: {} },
		specsEndpoint: '',
		specsCache   : {},
		loadingIds   : [],
		columns      : [ null, null, null ],
		maxColumns   : MAX_COLUMNS_DEFAULT,
		isMobile     : false,

		// Which column's dropdown is open ( only ever one ), and the position of
		// the highlighted option within that panel. Position 0 is the "Select a
		// product…" clear row; product options follow in series order.
		openIndex  : null,
		activeIndex: 0,

		init() {
			const island = this.$refs.compareData;

			if ( island ) {
				this.specsEndpoint = island.dataset.specsEndpoint || '';
				this.maxColumns    = parseInt( island.dataset.maxColumns, 10 ) || MAX_COLUMNS_DEFAULT;

				try {
					this.data = JSON.parse( island.textContent );
				} catch {
					this.data = { series: [], products: {} };
				}
			}

			this.columns = Array( this.maxColumns ).fill( null );

			// Track the viewport so the visible-column count can drop to two on
			// mobile without a horizontal scroll.
			const query = window.matchMedia( '(max-width: 63.999rem)' );

			this.isMobile = query.matches;
			query.addEventListener( 'change', ( event ) => {
				this.isMobile = event.matches;

				// A column can drop out of view at this breakpoint; don't leave
				// its panel open ( and unreachable ) behind it.
				this.closeDropdown();
			});
		},

		// The number of product columns to render: two on mobile, otherwise the
		// full desktop count.
		visibleCount() {
			return this.isMobile ? Math.min( MOBILE_COLUMNS, this.maxColumns ) : this.maxColumns;
		},

		// Column indexes to iterate in the template.
		visibleColumns() {
			return Array.from({ length: this.visibleCount() }, ( _, index ) => index );
		},

		// Whether any visible column has a product chosen yet. Drives the
		// instructional empty state before the first pick.
		anySelected() {
			return this.visibleColumns().some( ( index ) => this.columns[ index ] );
		},

		// Resolve a column's selected product to its island object, or null when
		// the column is still empty.
		columnProduct( index ) {
			const id = this.columns[ index ];

			return id ? this.data.products[ id ] || null : null;
		},

		/**
		 * Every option in panel order: the clear row first, then each series'
		 * products. Positions in this list drive keyboard navigation and the
		 * option ids that aria-activedescendant points at.
		 */
		flatOptions() {
			return [ null, ...this.data.series.flatMap( ( series ) => series.products ) ];
		},

		optionCount() {
			return this.flatOptions().length;
		},

		optionPosition( productId ) {
			return this.flatOptions().indexOf( productId );
		},

		optionId( column, position ) {
			return `compare-column-${ column }-option-${ position }`;
		},

		isOpen( index ) {
			return this.openIndex === index;
		},

		/**
		 * Open a column's panel with the highlight on its current product, so
		 * arrowing starts from what is already chosen.
		 */
		openDropdown( index ) {
			this.openIndex   = index;
			this.activeIndex = Math.max( 0, this.optionPosition( this.columns[ index ] ) );

			this.scrollActiveIntoView();
		},

		// Close the open panel. Passing the column index returns focus to that
		// column's trigger ( keyboard dismissal ); omitting it does not ( a click
		// elsewhere on the page should land where the user clicked ).
		closeDropdown( index ) {
			if ( null === this.openIndex ) {
				return;
			}

			const previous = this.openIndex;

			this.openIndex = null;

			if ( index === previous ) {
				document.getElementById( `compare-column-${ previous }` )?.focus();
			}
		},

		/**
		 * Dismiss the open panel on a click anywhere outside the pickers. Bound
		 * once at the window rather than per column: a per-column `.outside`
		 * handler would see a click on another column's trigger as "outside" and
		 * close the panel that click just opened.
		 */
		closeOnOutsideClick( event ) {
			if ( null !== this.openIndex && ! event.target.closest( '.compare-select' ) ) {
				this.closeDropdown();
			}
		},

		toggleDropdown( index ) {
			if ( this.isOpen( index ) ) {
				this.closeDropdown( index );
			} else {
				this.openDropdown( index );
			}
		},

		setActive( position ) {
			this.activeIndex = position;
		},

		// Move the highlight, wrapping at both ends.
		moveActive( delta ) {
			const count = this.optionCount();

			this.activeIndex = ( this.activeIndex + delta + count ) % count;

			this.scrollActiveIntoView();
		},

		chooseActive( index ) {
			this.choose( index, this.flatOptions()[ this.activeIndex ] );
		},

		choose( index, productId ) {
			this.swap( index, productId );
			this.closeDropdown( index );
		},

		// Keep the highlighted row visible in a scrolled panel. Deferred so the
		// row exists ( and carries its new class ) before it is measured.
		scrollActiveIntoView() {
			this.$nextTick( () => {
				document
					.getElementById( this.optionId( this.openIndex, this.activeIndex ) )
					?.scrollIntoView({ block: 'nearest' });
			});
		},

		swap( index, productId ) {
			const id = productId || null;

			this.columns[ index ] = id;

			if ( id ) {
				this.ensureSpecs( id );
			}
		},

		specsFor( id ) {
			return this.specsCache[ id ] || [];
		},

		isLoading( id ) {
			return this.loadingIds.includes( id );
		},

		/**
		 * Fetch specifications for a single product unless they are already
		 * cached or in flight. Loading state is tracked per product id so
		 * swapping one column never blanks another that is already loaded. On
		 * failure an empty array is cached so the column shows the "no
		 * specifications" state rather than spinning forever.
		 */
		ensureSpecs( id ) {
			if ( ! id || this.specsCache[ id ] || this.isLoading( id ) || ! this.specsEndpoint ) {
				return;
			}

			this.loadingIds = [ ...this.loadingIds, id ];

			const url  = `${ this.specsEndpoint }?ids=${ id }`;
			const done = () => {
				this.loadingIds = this.loadingIds.filter( ( loadingId ) => loadingId !== id );
			};

			fetch( url, { headers: { Accept: 'application/json' } })
				.then( ( response ) => {
					if ( ! response.ok ) {
						throw new Error( `Compare specs request failed: ${ response.status }` );
					}

					return response.json();
				})
				.then( ( specs ) => {
					this.specsCache[ id ] = specs[ id ] || [];
				})
				.catch( () => {
					this.specsCache[ id ] = [];
				})
				.finally( done );
		},

	}) );
});
